/**
 * star_catalog — the night sky's STAR CATALOGUE (2026-09-11).
 *
 * The astrophoto is split in two (tools/asset-pipeline/extract-sky-stars.mjs):
 * a smooth STARLESS background that stays an equirect texture, and every
 * resolved star as a record (direction, flux, PSF width, colour) that the
 * shader draws as a ROUND POINT ON THE SPHERE. Points have no projection, so
 * the equirect pole squeeze (round texels → radial ellipses at the zenith)
 * cannot happen to them, and they are crisp at any screen size — the two
 * complaints the 8K texture could never answer (its stars are the upscaler's
 * streaky dashes at native texels; no detail exists past 4K).
 *
 * This file is PURE (no three import): the binary parser, the cube-face cell
 * grid and the bucketing that lets the shader fetch ONE cell per pixel — every
 * star is inserted into every cell its reach disc touches, so no 3×3
 * neighbourhood walk and no popping at cell edges. Node-tested by
 * verify:star-catalog. The rendering constants below are MIRRORED in
 * star_field.ts (the reach the bucketing uses must be the radius the shader
 * fades to zero at — the no-popping law).
 */

import { catalogMagnitudes, packMagPhase } from './night_sky_model.js';

export const STAR_CATALOG_VERSION = 1;
export const STAR_RECORD_BYTES = 12;
/** Flux code → sr·radiance (log scale, see the tool's header). */
export const FLUX_MIN = 1e-9;
export const FLUX_MAX = 1e-3;

/** Cells per cube-face edge (6·G² cells; ≈ 1.2° at a face centre, 0.6° at an edge). */
export const STAR_GRID = 96;
/** Star-record texture width in texels (two RGBA32F texels per entry). */
export const STAR_TEX_WIDTH = 2048;
/**
 * Floats per bucketed entry: [x, y, z, σ] [packedRGB, flux, reach, magPhase]. magPhase (night-sky realism wave,
 * 2026-10-06) = the star's visual magnitude (by rank — night_sky_model.catalogMagnitudes) packed with its twinkle phase
 * (`packMagPhase`); the space sky reads the first seven floats only.
 */
export const STAR_RECORD_FLOATS = 8;

// ── rendering constants (mirrored in star_field.ts) ──────────────────────────
/** σ_eff = max(σ·SHRINK, pixel floor): a tighter core than the photo's blur. */
export const STAR_SHRINK = 0.5;
/** Halo peak fraction: a soft photographic skirt exp(−d/σ) around the core. */
export const STAR_HALO = 0.05;
/** Radiance below which the halo is treated as gone (sets the reach). */
export const HALO_FLOOR = 0.0005;
/** Smallest reach — covers the ≥1-px core floor on small viewports. */
export const REACH_MIN = (0.3 * Math.PI) / 180;

export interface StarCatalog {
  count: number;
  /** Unit directions, 3 per star (three's equirectUV frame: +y = zenith). */
  dir: Float32Array;
  /** PSF σ in radians. */
  sigma: Float32Array;
  /** Flux in sr·linear-radiance (peak = flux / (2π σ_eff²)). */
  flux: Float32Array;
  /** Luminance-normalised linear colour, 3 per star. */
  color: Float32Array;
  /** Visual magnitude by flux rank (night_sky_model.catalogMagnitudes; the photo's flux is saturated at the top). */
  mag?: Float32Array;
}

/** three's equirectUV inverse: u = atan2(z, x)/2π + 0.5, v = asin(y)/π + 0.5. */
export function equirectDirection(u: number, v: number): [number, number, number] {
  const phi = (u - 0.5) * Math.PI * 2;
  const lat = (v - 0.5) * Math.PI;
  const c = Math.cos(lat);
  return [c * Math.cos(phi), Math.sin(lat), c * Math.sin(phi)];
}

export function decodeFlux(code: number): number {
  return FLUX_MIN * Math.pow(FLUX_MAX / FLUX_MIN, code / 65535);
}

export function parseStarCatalog(buf: ArrayBuffer): StarCatalog {
  const dv = new DataView(buf);
  if (buf.byteLength < 16 || dv.getUint32(0, true) !== 0x52545341 /* 'ASTR' LE */) throw new Error('star catalogue: bad magic');
  const version = dv.getUint32(4, true);
  if (version !== STAR_CATALOG_VERSION) throw new Error(`star catalogue: version ${version} (expected ${STAR_CATALOG_VERSION})`);
  const count = dv.getUint32(8, true);
  if (buf.byteLength < 16 + count * STAR_RECORD_BYTES) throw new Error('star catalogue: truncated');
  const dir = new Float32Array(count * 3), sigma = new Float32Array(count), flux = new Float32Array(count), color = new Float32Array(count * 3);
  for (let k = 0, o = 16; k < count; k++, o += STAR_RECORD_BYTES) {
    const u = dv.getUint16(o, true) / 65535, v = dv.getUint16(o + 2, true) / 65535;
    const d = equirectDirection(u, v);
    dir[k * 3] = d[0]; dir[k * 3 + 1] = d[1]; dir[k * 3 + 2] = d[2];
    sigma[k] = dv.getUint16(o + 4, true) * 1e-5;
    flux[k] = decodeFlux(dv.getUint16(o + 6, true));
    color[k * 3] = dv.getUint8(o + 8) / 100; color[k * 3 + 1] = dv.getUint8(o + 9) / 100; color[k * 3 + 2] = dv.getUint8(o + 10) / 100;
  }
  return { count, dir, sigma, flux, color, mag: catalogMagnitudes(flux) };
}

/**
 * The angular radius (chord ≈ radians) beyond which the shader draws nothing
 * of this star: the Gaussian core's 3.2σ, the halo's fall below HALO_FLOOR at
 * its brightest possible peak (σ_eff = σ·SHRINK), or REACH_MIN.
 */
export function starReach(sigma: number, flux: number): number {
  const sigE = sigma * STAR_SHRINK;
  const peak = flux / (2 * Math.PI * sigE * sigE);
  const haloExt = Math.max(0, Math.log((peak * STAR_HALO) / HALO_FLOOR)) * sigma;
  return Math.max(REACH_MIN, 3.2 * sigma, haloExt);
}

/** Cube face of a direction: 0 +x, 1 −x, 2 +y, 3 −y, 4 +z, 5 −z. */
export function cubeFace(x: number, y: number, z: number): number {
  const ax = Math.abs(x), ay = Math.abs(y), az = Math.abs(z);
  if (ax >= ay && ax >= az) return x > 0 ? 0 : 1;
  if (ay >= az) return y > 0 ? 2 : 3;
  return z > 0 ? 4 : 5;
}

/** Face-local (s, t) in [−1, 1] of a direction PROJECTED onto the given face's plane. */
export function faceST(face: number, x: number, y: number, z: number): [number, number] {
  const m = face < 2 ? Math.abs(x) : face < 4 ? Math.abs(y) : Math.abs(z);
  const s = face < 2 ? y : x;
  const t = face < 4 ? z : y;
  return [s / m, t / m];
}

const cellIndex = (face: number, s: number, t: number, G: number): number => {
  const i = Math.min(G - 1, Math.max(0, Math.floor((s + 1) * 0.5 * G)));
  const j = Math.min(G - 1, Math.max(0, Math.floor((t + 1) * 0.5 * G)));
  return face * G * G + j * G + i;
};

/** The cell a direction falls in (what the shader computes per pixel). */
export function cubeCellOf(x: number, y: number, z: number, G: number): number {
  const f = cubeFace(x, y, z);
  const [s, t] = faceST(f, x, y, z);
  return cellIndex(f, s, t, G);
}

const BOUNDARY_SAMPLES = 24;
/** Per-face (i0, i1, j0, j1) boxes for one star — scratch, reused across calls. */
const faceBox = new Int32Array(24);
const faceHit = new Uint8Array(6);

/**
 * Visit every cell a disc of angular radius `reach` around a direction can
 * touch: the disc's centre + boundary samples are grouped by face, and each
 * face's (i, j) bounding box of its samples is filled — a superset of the
 * cells the disc overlaps (the chord-vs-arc sliver between samples is < 1 %
 * of reach). Allocation-free (143k stars are bucketed at boot).
 */
export function forEachCellWithinReach(x: number, y: number, z: number, reach: number, G: number, visit: (cell: number) => void): void {
  // Tangent basis.
  const ux = Math.abs(y) < 0.9 ? 0 : 1, uy = Math.abs(y) < 0.9 ? 1 : 0;
  let e1x = uy * z, e1y = -ux * z, e1z = ux * y - uy * x; // up × d
  const l1 = Math.sqrt(e1x * e1x + e1y * e1y + e1z * e1z) || 1; e1x /= l1; e1y /= l1; e1z /= l1; // (Math.hypot is slow in V8)
  const e2x = y * e1z - z * e1y, e2y = z * e1x - x * e1z, e2z = x * e1y - y * e1x; // d × e1
  const cr = Math.cos(reach), sr = Math.sin(reach);
  faceHit.fill(0);
  const sample = (px: number, py: number, pz: number): void => {
    const ax = Math.abs(px), ay = Math.abs(py), az = Math.abs(pz);
    let f: number, m: number, s: number, t: number;
    if (ax >= ay && ax >= az) { f = px > 0 ? 0 : 1; m = ax; s = py; t = pz; }
    else if (ay >= az) { f = py > 0 ? 2 : 3; m = ay; s = px; t = pz; }
    else { f = pz > 0 ? 4 : 5; m = az; s = px; t = py; }
    const i = Math.min(G - 1, Math.max(0, Math.floor((s / m + 1) * 0.5 * G)));
    const j = Math.min(G - 1, Math.max(0, Math.floor((t / m + 1) * 0.5 * G)));
    const o = f * 4;
    if (!faceHit[f]) { faceHit[f] = 1; faceBox[o] = i; faceBox[o + 1] = i; faceBox[o + 2] = j; faceBox[o + 3] = j; }
    else { if (i < faceBox[o]) faceBox[o] = i; if (i > faceBox[o + 1]) faceBox[o + 1] = i; if (j < faceBox[o + 2]) faceBox[o + 2] = j; if (j > faceBox[o + 3]) faceBox[o + 3] = j; }
  };
  sample(x, y, z);
  for (let k = 0; k < BOUNDARY_SAMPLES; k++) {
    const a = (k / BOUNDARY_SAMPLES) * Math.PI * 2, ca = Math.cos(a), sa = Math.sin(a);
    sample(x * cr + (e1x * ca + e2x * sa) * sr, y * cr + (e1y * ca + e2y * sa) * sr, z * cr + (e1z * ca + e2z * sa) * sr);
  }
  for (let f = 0; f < 6; f++) {
    if (!faceHit[f]) continue;
    const o = f * 4, base = f * G * G;
    for (let j = faceBox[o + 2]; j <= faceBox[o + 3]; j++) for (let i = faceBox[o]; i <= faceBox[o + 1]; i++) visit(base + j * G + i);
  }
}

/** The cell set a reach disc touches (the harness's view of `forEachCellWithinReach`). */
export function cellsWithinReach(x: number, y: number, z: number, reach: number, G: number, out: Set<number>): void {
  forEachCellWithinReach(x, y, z, reach, G, (c) => out.add(c));
}

export interface StarBuckets {
  grid: number;
  /** 6·G² × 4 floats: [entry start, entry count, 0, 0] per cell (a G-high, 6G-wide texture). */
  cells: Float32Array;
  cellsWidth: number;
  cellsHeight: number;
  /** Entries × 8 floats, laid out as a STAR_TEX_WIDTH-wide RGBA32F texture (2 texels per entry). */
  records: Float32Array;
  recordsWidth: number;
  recordsHeight: number;
  entries: number;
  occupied: number;
  maxInCell: number;
  /** Stars dropped by the per-cell cap (the faintest — the catalogue is flux-sorted). */
  capped: number;
}

/** Pack a luminance-normalised colour into one exact float (3 × 8 bits, ×100). */
export function packColor(r: number, g: number, b: number): number {
  const q = (c: number): number => Math.min(255, Math.max(0, Math.round(c * 100)));
  return q(r) * 65536 + q(g) * 256 + q(b);
}

/**
 * Bucket a flux-sorted catalogue into the cube grid. Cell lists inherit the
 * catalogue order (bright first), so `maxPerCell` drops only the faintest.
 */
export function bucketStars(cat: StarCatalog, G: number = STAR_GRID, maxPerCell = 64): StarBuckets {
  const nCells = 6 * G * G;
  const reach = new Float32Array(cat.count);
  const mag = cat.mag ?? catalogMagnitudes(cat.flux);
  // Pass 1: how many stars touch each cell (a cell's list keeps catalogue
  // order — bright first — so the cap drops only the faintest).
  const full = new Uint32Array(nCells);
  // Each star's cell list is kept (flat, grown by doubling) so pass 2 never
  // re-samples the discs.
  const starStart = new Uint32Array(cat.count + 1);
  let starCells = new Uint32Array(Math.max(1024, cat.count * 4)), total = 0;
  for (let k = 0; k < cat.count; k++) {
    reach[k] = starReach(cat.sigma[k], cat.flux[k]);
    starStart[k] = total;
    forEachCellWithinReach(cat.dir[k * 3], cat.dir[k * 3 + 1], cat.dir[k * 3 + 2], reach[k], G, (c) => {
      full[c]++;
      if (total === starCells.length) { const g = new Uint32Array(starCells.length * 2); g.set(starCells); starCells = g; }
      starCells[total++] = c;
    });
  }
  starStart[cat.count] = total;
  let entries = 0, occupied = 0, maxInCell = 0, capped = 0;
  const cellsWidth = 6 * G, cellsHeight = G;
  const cells = new Float32Array(cellsWidth * cellsHeight * 4);
  const start = new Uint32Array(nCells), fill = new Uint32Array(nCells);
  for (let c = 0; c < nCells; c++) {
    const n = full[c];
    if (n > 0) { occupied++; if (n > maxInCell) maxInCell = n; if (n > maxPerCell) capped += n - maxPerCell; }
    const kept = Math.min(n, maxPerCell);
    start[c] = entries;
    const face = Math.floor(c / (G * G)), rem = c - face * G * G, j = Math.floor(rem / G), i = rem - j * G;
    const t = ((j * cellsWidth) + face * G + i) * 4;
    cells[t] = entries; cells[t + 1] = kept;
    entries += kept;
  }
  const recordsWidth = STAR_TEX_WIDTH;
  const recordsHeight = Math.max(1, Math.ceil((entries * 2) / recordsWidth));
  const records = new Float32Array(recordsWidth * recordsHeight * 4);
  // Pass 2: write the records in cell order.
  for (let k = 0; k < cat.count; k++) {
    const x = cat.dir[k * 3], y = cat.dir[k * 3 + 1], z = cat.dir[k * 3 + 2];
    const packed = packColor(cat.color[k * 3], cat.color[k * 3 + 1], cat.color[k * 3 + 2]);
    const phase = (k * 0.6180339887) % 1; // per-star twinkle phase (golden-ratio spread)
    const magPhase = packMagPhase(mag[k], phase);
    for (let q = starStart[k], qe = starStart[k + 1]; q < qe; q++) {
      const c = starCells[q];
      const n = fill[c]++;
      if (n >= maxPerCell) continue;
      const o = (start[c] + n) * STAR_RECORD_FLOATS;
      records[o] = x; records[o + 1] = y; records[o + 2] = z; records[o + 3] = cat.sigma[k];
      records[o + 4] = packed; records[o + 5] = cat.flux[k]; records[o + 6] = reach[k]; records[o + 7] = magPhase;
    }
  }
  return { grid: G, cells, cellsWidth, cellsHeight, records, recordsWidth, recordsHeight, entries, occupied, maxInCell, capped };
}
